import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';
import { HttpService } from '../../../services/http.service';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { StorageService } from '../../../services/storage.service';
import { UserAccountDTO } from '../../../classes/user-account.dto';
import { MedicalMisurationType } from '../../misuration/misuration.component';
import { DataService } from '../../../services/data.service';
import { MeasurementDTO, MedicalDataDTO } from '../../../classes/api-dto';
import format from 'date-fns/format';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { GtmService } from '../../../services/gtm.service';

@Component({
  selector: 'app-modal-add-value-form-by-id',
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, CommonModule, NzRadioModule, NzTimePickerModule, FormsModule],
  templateUrl: './modal-add-value-form-by-id.component.html',
  styleUrls: ['./modal-add-value-form-by-id.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ModalAddValueFormByIdComponent implements OnInit {
  readonly nzModalData = inject(NZ_MODAL_DATA);
  @Output() emitForNewValue: EventEmitter<string> = new EventEmitter();
  formModal!: FormGroup;
  dataModal?: { id: string; graph?: boolean };
  todayDate: Date = new Date();
  startDate: Date = new Date(1950);
  subscription: Subscription[] = [];
  medicalLimits: any = null;

  glycemiaFoodTexts: string[] = [];
  glycemiaFoodValues: string[] = [];
  glycemiaMeasurementTexts: string[] = [];
  glycemiaMeasurementValues: string[] = [];

  prevMedicalData?: MedicalDataDTO;

  bloodGroupData = ['0 Rh-', '0 Rh+', 'A Rh-', 'A Rh+', 'B Rh-', 'B Rh+', 'AB Rh-', 'AB Rh+'];

  constructor(
    private modalRef: NzModalRef,
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private http: HttpService,
    private storage: StorageService,
    private dataService: DataService,
    private gtmService: GtmService
  ) {}

  ngOnInit() {
    this.initGlycemiaSelectOptions();
    this.getPrevData();
    this.fetchMedicalLimits();
    this.dataModal = this.nzModalData.dataModal;

    this.formModal = this.fb.group({
      creationDate: [null, Validators.required],
      creationHour: [new Date(), Validators.required],
    });
  }

  private initGlycemiaSelectOptions(): void {
    this.glycemiaFoodTexts = ['A digiuno', '2 ore dopo il pasto'];
    this.glycemiaFoodValues = ['D', '2H'];

    this.glycemiaMeasurementTexts = ['mg/dL', 'mmol/L'];
    this.glycemiaMeasurementValues = ['MG', 'MM'];
  }

  private fetchMedicalLimits(): void {
    const individualId = JSON.parse(this.storage.getItem('userData')!)?.individualId;
    this.http.mainmedicalstastlimits(individualId).subscribe(
      (response) => {
        this.medicalLimits = response;
        if (this.dataModal?.id) {
          this.handleFormType();
        }
        this.cdr.detectChanges();
      },
      (error) => {
        console.error('Errore nel recupero dei limiti:', error);
      }
    );
  }

  private handleFormType(): void {
    switch (this.dataModal!.id) {
      case 'weight':
        this.formModal.addControl(
          'weight',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.weightLimit.min), Validators.max(this.medicalLimits.weightLimit.max)])
        );
        break;

      case 'height':
        this.formModal.addControl('height', new FormControl(null, Validators.required));
        break;

      case 'bpm':
        this.formModal.addControl(
          'bpm',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.bpmLimits.min), Validators.max(this.medicalLimits.bpmLimits.max)])
        );
        break;

      case 'waistCircumference':
      case 'hipCircumference':
      case 'wristCircumference':
        this.formModal.addControl(this.dataModal!.id, new FormControl(null, Validators.required));
        break;

      case 'temperature':
        this.formModal.addControl(
          'temperature',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.temperatureLimits.celsius.min),
            Validators.max(this.medicalLimits.temperatureLimits.celsius.max),
          ])
        );
        break;

      case 'isSmoker':
      case 'bloodGroup':
        this.formModal.addControl(this.dataModal!.id, new FormControl(null, Validators.required));
        this.formModal.removeControl('creationDate');
        this.formModal.removeControl('creationHour');
        break;

      case 'pressure':
        this.formModal.addControl(
          'maxValue',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.pressureLimits.systolic.min),
            Validators.max(this.medicalLimits.pressureLimits.systolic.max),
          ])
        );
        this.formModal.addControl(
          'minValue',
          new FormControl(null, [
            Validators.required,
            Validators.min(this.medicalLimits.pressureLimits.diastolic.min),
            Validators.max(this.medicalLimits.pressureLimits.diastolic.max),
          ])
        );
        this.formModal.addControl('pressureBpm', new FormControl(null, [Validators.min(this.medicalLimits.bpmLimits.min), Validators.max(this.medicalLimits.bpmLimits.max)]));
        break;

      case 'glycemia':
        this.formModal.addControl(
          'glycemia',
          new FormControl(null, [Validators.required, Validators.min(this.medicalLimits.glycemiaLimits.min), Validators.max(this.medicalLimits.glycemiaLimits.max)])
        );
        this.formModal.addControl('measureType', new FormControl(null, Validators.required));
        this.formModal.addControl('measureUnit', new FormControl(null, Validators.required));
        break;
    }

    this.checkValidationForm();
  }

  private checkValidationForm(): void {
    if (this.formModal) {
      this.subscription.push(
        this.formModal.valueChanges.subscribe(() => {
          this.cdr.detectChanges();
        })
      );
    }
  }

  getControl(id: string): FormControl {
    return this.formModal.get(id) as FormControl;
  }

  protected close(): void {
    this.modalRef.close();
  }

  private getPrevData(): void {
    this.dataService.getMedicalData().subscribe((data) => {
      this.prevMedicalData = data;
    });
  }

  protected register(): void {
    if (this.prevMedicalData) {
      this.prevMedicalData.lastMeasurement![this.dataModal!.id as keyof MeasurementDTO] = this.formModal.value[this.dataModal!.id];
      this.prevMedicalData.lastMeasurement!.lastTimeModified = this.formModal.value['creationDate']
        ? this.getModuledData(this.formModal.value)
        : format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSSSSSS");

      const medicalStatsParams: MedicalMisurationType[] = ['bpm', 'temperature', 'pressure', 'glycemia', 'weight', 'waistCircumference', 'hipCircumference', 'wristCircumference'];
      const userData = JSON.parse(this.storage.getItem('userData')!) as UserAccountDTO;
      const individualId = userData?.individualId;
      if (medicalStatsParams.includes(this.dataModal!.id as MedicalMisurationType) && this.dataModal?.graph) {
        if (this.formModal.value['creationDate']) {
          this.formModal.removeControl('creationHour');
        }
        const payload = {
          ...this.formModal.value,
          individualId: individualId,
        };
        this.http.updateMedicalStats(this.dataModal!.id as MedicalMisurationType, payload).subscribe(() => {
          this.dataService.setMedicalData({ firstMeasurement: this.prevMedicalData!.firstMeasurement, lastMeasurement: this.prevMedicalData!.lastMeasurement });
          this.gtmService.pushHealthDataEvent('register_misuration', this.dataModal!.id);
          this.modalRef.close(this.dataModal!.id);
        });
      } else {
        const payload = {
          ...this.prevMedicalData.lastMeasurement,
        };
        payload.contactId = individualId;
        this.http.updateMedicalProfileData(payload).subscribe(() => {
          this.dataService.setMedicalData({ firstMeasurement: this.prevMedicalData!.firstMeasurement, lastMeasurement: payload });
          this.gtmService.pushHealthDataEvent('register_medical_data', this.dataModal!.id);
          this.modalRef.close(this.dataModal!.id);
        });
      }
    }
  }

  private getModuledData(body: any) {
    const datePart = body?.creationDate;
    const timePart = body?.creationHour;
    if (datePart && timePart) {
      const updatedCreationDate = new Date(
        Date.UTC(datePart.getFullYear(), datePart.getMonth(), datePart.getDate(), timePart.getHours(), timePart.getMinutes(), timePart.getSeconds())
      );
      return updatedCreationDate;
    }
    return;
  }
}
